import axios from 'axios';
import { login, getAccessToken } from './authentication'
import Vue from 'vue';
import { set } from 'lodash';

/**
 * @summary API client
 */
export default class ApiClient {
  static _recentRequestCacheDefaultDuration = 1000 * 60 * 5; // 5 minutes
  static _recentRequestCacheLimit = 100;
  static _recentRequestsCache = {};

  static _disableCache = true;

  /**
   * @summary Get the client ID from the session
   * 
   * @returns {string}
   */
  static getClientID() {
    return sessionStorage.getItem('clientID');
  }

  /**
   * @summary Set the client ID in the session
   * 
   * @param {string} clientID
   * 
   * @returns {void}
   */
  static setClientID(clientID) {
    sessionStorage.setItem('clientID', clientID);
  }

  /**
   * @summary Make a GET request to the specified URL
   * 
   * @param {string} url 
   * @param {import('axios').AxiosRequestConfig} config 
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  static async get(url, config) {
    return await _withErrorHandling(async () => {
      config = config || {};
      await _setAuthenticationHeader(config);
      _setXClientHeader(config);
      const response = await axios.get(url, config);
      return response;
    });
  }

  static async getWithCache(url, config) {
    if (ApiClient._disableCache) {
      return await ApiClient.get(url, config);
    }

    return await this.forceGetWithCache(url, config);
  }

  static async forceGetWithCache(url, config) {
    return await _withErrorHandling(async () => {
      config = config || {};
      const duration = config['cacheDuration'] || ApiClient._recentRequestCacheDefaultDuration;
      delete config['cacheDuration'];
      await _setAuthenticationHeader(config);
      _setXClientHeader(config);
      const cachedResponse = ApiClient._getCachedResponse(url, config, duration);
      if (cachedResponse) {
        return cachedResponse;
      }

      const response = await axios.get(url, config);
      ApiClient._cacheResponse(url, config, response, duration);
      return response;
    });
  }

  static clearCache(urlPrefix) {
    for (const key in ApiClient._recentRequestsCache) {
      if (key.startsWith(urlPrefix)) {
        delete ApiClient._recentRequestsCache[key];
      }
    }
  }

  static _getCachedResponse(url, config, duration) {
    const cacheKey = ApiClient._getCacheKey(url, config);
    const cachedResponse = ApiClient._recentRequestsCache[cacheKey];
    if (cachedResponse && cachedResponse.timestamp + duration > Date.now()) {
      return cachedResponse.response;
    }
    return null;
  }

  static _cacheResponse(url, config, response, duration) {
    const cacheKey = ApiClient._getCacheKey(url, config);
    ApiClient._recentRequestsCache[cacheKey] = {
      timestamp: Date.now(),
      response
    };
    setTimeout(() => {
      delete ApiClient._recentRequestsCache[cacheKey];      
    }, duration);
    if (Object.keys(ApiClient._recentRequestsCache).length > ApiClient._recentRequestCacheLimit) {
      setTimeout(() => ApiClient._deleteOldCacheEntries(), 0);
    }
  }

  static _deleteOldCacheEntries() {
    const sortedKeys = Object.keys(ApiClient._recentRequestsCache).sort((a, b) => {
      return ApiClient._recentRequestsCache[a].timestamp - ApiClient._recentRequestsCache[b].timestamp;
    });

    // Remove the oldest 25% of the cache
    const keysToRemove = sortedKeys.slice(0, sortedKeys.length / 4);
    for (const key of keysToRemove) {
      delete ApiClient._recentRequestsCache[key];
    }
  }

  static _getCacheKey(url, config) {
    return `${url}::${JSON.stringify(config)}`;
  }


  /**
   * @summary Make a POST request to the specified URL
   * 
   * @param {string} url 
   * @param {any} data 
   * @param {import('axios').AxiosRequestConfig} config 
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  static async post(url, data, config) {
    return await _withErrorHandling(async () => {
      config = config || {};
      await _setAuthenticationHeader(config);
      _setXClientHeader(config);
      const response = await axios.post(url, data, config);
      return response;
    });
  }

  /**
   * @summary Make a PUT request to the specified
   * 
   * @param {string} url 
   * @param {any} data 
   * @param {import('axios').AxiosRequestConfig} config 
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  static async put(url, data, config) {
    return await _withErrorHandling(async () => {
      config = config || {};
      await _setAuthenticationHeader(config);
      _setXClientHeader(config);
      const response = await axios.put(url, data, config);
      return response;
    });
  }

  /**
   * @summary Make a DELETE request to the specified URL
   * 
   * @param {string} url
   * @param {import('axios').AxiosRequestConfig} config
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  static async delete(url, config) {
    return await _withErrorHandling(async () => {
      config = config || {};
      await _setAuthenticationHeader(config);
      _setXClientHeader(config);
      const response = await axios.delete(url, config);
      return response;
    });
  }

  /**
   * @summary Get the axios instance
   * 
   * @returns {import('axios').AxiosInstance}
   */
  static get axios() {
    return axios;
  }

  /**
   * Download a file from the specified URL
   * @param {string} url 
   * @param {string} filename 
   * @returns {Promise<void>}
   */
  static async downloadFile(url, filename) {
    return await _withErrorHandling(async () => {
      if (!url || !filename) {
        throw new Error("URL and filename are required for file download");
      }

      const config = {};
      await _setAuthenticationHeader(config);
      _setXClientHeader(config);
      const response = await fetch(url, {
        method: 'GET',
        headers: config.headers
      });
      
      if (response.status !== 200) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    });
  }
}

/**
 * @summary Handle errors from the API
 * 
 * @param {() => Promise<any>} func
 * @returns {Promise<any>}
 */
async function _withErrorHandling(func) {
  try {
    return await func();
  }
  catch (error) {
    Vue.notify({
      type: 'error',
      title: 'Request Error',
      text: error.response?.data?.message || error.response?.data || error.message,
      duration: 30 * 1000
    })
    throw error;
  }
}

/**
 * @summary Attempt to login
 * 
 * @returns {Promise<void>}
 */
async function _attemptLogin() {
  const loginResponse = await login();
  if (!loginResponse) {
    throw new Error("Unauthorized");
  }
}

/**
 * @summary Set the authentication header
 * 
 * @param {import('axios').AxiosRequestConfig} config
 */
async function _setAuthenticationHeader(config) {
  const accessToken = await getAccessToken();

  if (!accessToken) {
    console.log("No access token");
    await _attemptLogin();
    accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error("Unauthorized");
    }
  }
  config.headers = config.headers || {};
  config.headers.Authorization = `Bearer ${await getAccessToken()}`;
}

/**
 * @summary Set the authentication header
 * 
 * @param {import('axios').AxiosRequestConfig} config
 */
function _setXClientHeader(config) {
  const clientID = ApiClient.getClientID();
  if (clientID) {
    config.headers = config.headers || {};
    config.headers['X-Client'] = ApiClient.getClientID();
  }
}